import {useState} from "react";
import {AlertState, SubscriptionAlertConfig} from "./AlertSubscriptionsManagement";
import {CHECKBOX_PARTIAL} from "@renta-apps/renta-react-components";
import {SubscriptionAlertType} from "@/models/Enums";
import BaseAlarmDeviceModel from "@/models/BaseAlarmDeviceModel";
import BaseAlarmModel from "@/models/BaseAlarmModel";
import AlarmData from "@/models/server/AlarmData";
import {AlarmTypeHelper} from "@/helpers/AlarmTypeHelper";

type UseAlertsSubscriptionsManagementStateReturn = [
    (SubscriptionAlertConfig[] | null),
    (type: SubscriptionAlertType, value: AlertState) => void,
    (type: SubscriptionAlertType, value: AlarmData) => void,
    (updater: SubscriptionAlertConfig[] | null | (() => SubscriptionAlertConfig[])) => void,
];

export const selectedDevicesInitializer = (selectedDevices: BaseAlarmDeviceModel<BaseAlarmModel>[]): SubscriptionAlertConfig[] => {
    return AlarmTypeHelper.selectedDevicesConfig.map(config => {
        const configuredDevices = selectedDevices.filter(config.getConfiguredAlert);
        const configuredDevicesCount = configuredDevices.length;
        const supportedDevices = selectedDevices.filter(config.supported).length;
        const alarmData = configuredDevicesCount === 1
            ? config.getConfiguredAlert(configuredDevices[0])?.alarmData ?? undefined
            : undefined;

        return {
            type: config.type,
            supported: supportedDevices > 0,
            enabled: configuredDevicesCount === 0 ? false : configuredDevicesCount === supportedDevices ? true : CHECKBOX_PARTIAL,
            numberOfDevices: supportedDevices,
            alarmData,
        };
    });
};


export const useAlertsSubscriptionsManagementState = (
    initializer: SubscriptionAlertConfig[] | null | (() => SubscriptionAlertConfig[])
): UseAlertsSubscriptionsManagementStateReturn => {
    const [subscriptions, setSubscriptionsInternal] = useState<SubscriptionAlertConfig[] | null>(initializer);

    const updateSubscription = (type: SubscriptionAlertType, value: AlertState) => {
        setSubscriptionsInternal(prevSubscriptions => {
            return prevSubscriptions?.map(subscription => {
                if (subscription.type === type) {
                    return {
                        ...subscription,
                        enabled: value,
                        alarmData: value === false ? undefined : subscription.alarmData,
                    };
                }
                return subscription;
            }) ?? null;
        });
    };

    const updateAlarmData = (type: SubscriptionAlertType, value: AlarmData) => {
        setSubscriptionsInternal(prevSubscriptions => {
            return prevSubscriptions?.map(subscription => {
                if (subscription.type === type) {
                    return {
                        ...subscription,
                        alarmData: {
                            ...(subscription.alarmData ?? {}),
                            ...value,
                        },
                    };
                }
                return subscription;
            }) ?? null;
        });
    };

    const setSubscriptions = (updater: SubscriptionAlertConfig[] | null | (() => SubscriptionAlertConfig[])) => {
        setSubscriptionsInternal(updater);
    };

    return [subscriptions, updateSubscription, updateAlarmData, setSubscriptions];
};